import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { Plus, Pencil, Trash2, Truck, Search, Factory, MoreHorizontal, Wrench } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import EquipmentEditDrawer from '../components/EquipmentEditDrawer';

const Equipment = () => {
  const { api } = useAuth();
  const { currentTier } = useTier();
  const [activeTab, setActiveTab] = useState('equipment');
  
  // Equipment state
  const [equipment, setEquipment] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'equipment' or 'attachment'
  const [isDeleting, setIsDeleting] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [attachmentSearchTerm, setAttachmentSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [attachmentForm, setAttachmentForm] = useState({
    name: '',
    attachment_type_id: '',
    size: '',
    status: 'available',
    notes: ''
  });

  // New state for makes and models
  const [equipmentMakes, setEquipmentMakes] = useState([]);
  const [equipmentModels, setEquipmentModels] = useState([]);

  const fetchData = async () => {
    try {
      const [equipRes, suppRes, typesRes, attachmentsRes, attachmentTypesRes, makesRes] = await Promise.all([
        api.get('/equipment'),
        api.get('/suppliers'),
        api.get('/equipment-types'),
        api.get('/attachments'),
        api.get('/attachment-types'),
        api.get('/equipment-makes')
      ]);
      setEquipment(equipRes.data);
      setSuppliers(suppRes.data);
      setEquipmentTypes(typesRes.data);
      setAttachments(attachmentsRes.data);
      setAttachmentTypes(attachmentTypesRes.data);
      setEquipmentMakes(makesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchModelsForMake = async (makeId) => {
    try {
      const res = await api.get(`/equipment-models?make_id=${makeId}`);
      setEquipmentModels(res.data);
    } catch (error) {
      console.error('Failed to fetch models:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  // Auto-select supplier for Basic tier with single supplier
  const defaultSupplierId = useMemo(() => {
    if (currentTier === 'basic' && suppliers.length === 1) {
      return suppliers[0].id;
    }
    return null;
  }, [currentTier, suppliers]);

  // Equipment handlers
  const handleOpenDialog = async (equip = null) => {
    setEditingEquipment(equip);
    // Pre-fetch models if equipment has a make - wait for completion
    if (equip?.make_id) {
      await fetchModelsForMake(equip.make_id);
    } else {
      setEquipmentModels([]);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (formData, equipment) => {
    try {
      // Extract local photos before submit
      const localPhotos = formData._localPhotos || [];
      delete formData._localPhotos;
      
      let equipmentId;
      if (equipment) {
        await api.put(`/equipment/${equipment.id}`, formData);
        equipmentId = equipment.id;
        toast.success('Asset updated');
      } else {
        const res = await api.post('/equipment', formData);
        equipmentId = res.data.id;
        toast.success('Asset added');
      }
      
      // Upload local photos for new equipment
      if (localPhotos.length > 0 && equipmentId) {
        for (const file of localPhotos) {
          await handlePhotoUpload(equipmentId, file);
        }
      }
      
      setDialogOpen(false);
      setEditingEquipment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  // Make/Model handlers
  const handleCreateMake = async (makeData) => {
    try {
      const res = await api.post('/equipment-makes', makeData);
      setEquipmentMakes([...equipmentMakes, res.data]);
      toast.success('Make created');
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create make');
      return null;
    }
  };

  const handleCreateModel = async (modelData) => {
    try {
      const res = await api.post('/equipment-models', modelData);
      setEquipmentModels([...equipmentModels, res.data]);
      toast.success('Model created');
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create model');
      return null;
    }
  };

  // Photo handlers
  const handlePhotoUpload = async (equipmentId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/equipment/${equipmentId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded');
      return res.data.photo;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
      return null;
    }
  };

  const handlePhotoDelete = async (equipmentId, photoIndex) => {
    try {
      await api.delete(`/equipment/${equipmentId}/photos/${photoIndex}`);
      toast.success('Photo deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete photo');
      throw error;
    }
  };

  const handleDelete = async (id) => {
    const equipmentItem = equipment.find(e => e.id === id);
    setItemToDelete(equipmentItem);
    setDeleteType('equipment');
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      if (deleteType === 'equipment') {
        await api.delete(`/equipment/${itemToDelete.id}`);
        toast.success('Asset deleted');
      } else if (deleteType === 'attachment') {
        await api.delete(`/attachments/${itemToDelete.id}`);
        toast.success('Attachment deleted');
      }
      fetchData();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateSupplier = async (supplierData) => {
    try {
      const response = await api.post('/suppliers', supplierData);
      toast.success('Supplier created');
      const suppRes = await api.get('/suppliers');
      setSuppliers(suppRes.data);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create supplier');
      return null;
    }
  };

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.company_name || '-';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <Badge className="status-available">Available</Badge>;
      case 'booked':
        return <Badge className="status-booked">Booked</Badge>;
      case 'out_of_service':
        return <Badge className="status-out-of-service">Out of Service</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Attachment handlers
  const resetAttachmentForm = () => {
    setAttachmentForm({
      name: '',
      attachment_type_id: '',
      size: '',
      status: 'available',
      notes: ''
    });
    setEditingAttachment(null);
  };

  const handleOpenAttachmentDialog = (attachment = null) => {
    if (attachment) {
      setEditingAttachment(attachment);
      setAttachmentForm({
        name: attachment.name,
        attachment_type_id: attachment.attachment_type_id,
        size: attachment.size || '',
        status: attachment.status,
        notes: attachment.notes || ''
      });
    } else {
      resetAttachmentForm();
    }
    setAttachmentDialogOpen(true);
  };

  const handleAttachmentSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAttachment) {
        await api.put(`/attachments/${editingAttachment.id}`, attachmentForm);
        toast.success('Attachment updated');
      } else {
        await api.post('/attachments', attachmentForm);
        toast.success('Attachment created');
      }
      setAttachmentDialogOpen(false);
      resetAttachmentForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleAttachmentDelete = async (attachment) => {
    setItemToDelete(attachment);
    setDeleteType('attachment');
    setDeleteDialogOpen(true);
  };

  const getAttachmentTypeName = (typeId) => {
    const type = attachmentTypes.find(t => t.id === typeId);
    return type ? type.name : 'Unknown';
  };

  const getAttachmentStatusBadge = (status) => {
    const styles = {
      available: 'bg-green-500/20 text-green-500',
      in_use: 'bg-blue-500/20 text-blue-500',
      under_repair: 'bg-amber-500/20 text-amber-500',
      retired: 'bg-gray-500/20 text-gray-500'
    };
    const labels = {
      available: 'Available',
      in_use: 'In Use',
      under_repair: 'Under Repair',
      retired: 'Retired'
    };
    return <Badge className={styles[status] || 'bg-gray-500/20'}>{labels[status] || status}</Badge>;
  };

  const filteredEquipment = equipment.filter(eq =>
    eq.asset_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    eq.equipment_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getSupplierName(eq.supplier_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAttachments = attachments.filter(attachment => {
    const matchesSearch = attachment.name.toLowerCase().includes(attachmentSearchTerm.toLowerCase()) ||
      (attachment.size && attachment.size.toLowerCase().includes(attachmentSearchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || attachment.status === statusFilter;
    const matchesType = typeFilter === 'all' || attachment.attachment_type_id === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Assets</h1>
          <p className="text-muted-foreground">Manage your fleet of machines and attachments</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="equipment" data-testid="tab-equipment">
            <Truck className="h-4 w-4 mr-2" />
            Machines
          </TabsTrigger>
          <TabsTrigger value="attachments" data-testid="tab-attachments">
            <Wrench className="h-4 w-4 mr-2" />
            Attachments
          </TabsTrigger>
        </TabsList>

        {/* Equipment Tab */}
        <TabsContent value="equipment" className="mt-6">
          <Card className="card-industrial">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="equipment-search"
                    placeholder="Search assets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-equipment-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Asset
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredEquipment.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No assets found</p>
                  <Button className="mt-4 btn-volt" onClick={() => handleOpenDialog()}>
                    Add Your First Asset
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="label-overline">Asset ID</TableHead>
                        <TableHead className="label-overline">Supplier</TableHead>
                        <TableHead className="label-overline">Type</TableHead>
                        <TableHead className="label-overline">Capacity</TableHead>
                        <TableHead className="label-overline">Status</TableHead>
                        <TableHead className="label-overline text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEquipment.map((eq) => (
                        <TableRow 
                          key={eq.id} 
                          data-testid={`equipment-row-${eq.id}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={(e) => {
                            // Don't trigger if clicking actions menu
                            if (e.target.closest('button')) return;
                            handleOpenDialog(eq);
                          }}
                        >
                          <TableCell className="font-mono text-sm font-medium">{eq.asset_id}</TableCell>
                          <TableCell>
                            {eq.supplier_id ? (
                              <span className="flex items-center gap-1 text-sm">
                                <Factory className="h-3 w-3 text-[#ffd108]" />
                                {getSupplierName(eq.supplier_id)}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{eq.equipment_type}</TableCell>
                          <TableCell>{eq.capacity || '-'}</TableCell>
                          <TableCell>{getStatusBadge(eq.status)}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  data-testid={`equipment-actions-${eq.id}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => handleOpenDialog(eq)}
                                  data-testid={`edit-equipment-${eq.id}`}
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Equipment
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDelete(eq.id)}
                                  className="text-destructive focus:text-destructive"
                                  data-testid={`delete-equipment-${eq.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Equipment
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attachments Tab */}
        <TabsContent value="attachments" className="mt-6">
          <Card className="card-industrial">
            <CardHeader>
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="attachment-search"
                    placeholder="Search attachments..."
                    value={attachmentSearchTerm}
                    onChange={(e) => setAttachmentSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="type-filter">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {attachmentTypes.map(type => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[140px]" data-testid="status-filter">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="in_use">In Use</SelectItem>
                      <SelectItem value="under_repair">Under Repair</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button className="btn-volt" onClick={() => handleOpenAttachmentDialog()} data-testid="add-attachment-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Attachment
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredAttachments.length === 0 ? (
                <div className="text-center py-12">
                  <Wrench className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No attachments found</p>
                  <Button className="mt-4 btn-volt" onClick={() => handleOpenAttachmentDialog()}>
                    Add Your First Attachment
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="label-overline">Name</TableHead>
                        <TableHead className="label-overline">Type</TableHead>
                        <TableHead className="label-overline">Size</TableHead>
                        <TableHead className="label-overline">Status</TableHead>
                        <TableHead className="label-overline">Notes</TableHead>
                        <TableHead className="label-overline text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAttachments.map((attachment) => (
                        <TableRow key={attachment.id} data-testid={`attachment-row-${attachment.id}`}>
                          <TableCell className="font-medium">{attachment.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{getAttachmentTypeName(attachment.attachment_type_id)}</Badge>
                          </TableCell>
                          <TableCell>{attachment.size || '-'}</TableCell>
                          <TableCell>{getAttachmentStatusBadge(attachment.status)}</TableCell>
                          <TableCell className="max-w-[200px] truncate text-muted-foreground">
                            {attachment.notes || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleOpenAttachmentDialog(attachment)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleAttachmentDelete(attachment)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Shared Equipment Edit Drawer */}
      <EquipmentEditDrawer
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        equipment={editingEquipment}
        suppliers={suppliers}
        equipmentTypes={equipmentTypes}
        equipmentMakes={equipmentMakes}
        equipmentModels={equipmentModels}
        onSubmit={handleSubmit}
        onSupplierCreate={handleCreateSupplier}
        onMakeCreate={handleCreateMake}
        onModelCreate={handleCreateModel}
        onPhotoUpload={handlePhotoUpload}
        onPhotoDelete={handlePhotoDelete}
        onFetchModels={fetchModelsForMake}
        defaultSupplierId={defaultSupplierId}
        api={api}
      />

      {/* Add/Edit Attachment Dialog */}
      <Dialog open={attachmentDialogOpen} onOpenChange={setAttachmentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingAttachment ? 'Edit Attachment' : 'Add Attachment'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAttachmentSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Name *</Label>
              <Input
                data-testid="attachment-name-input"
                value={attachmentForm.name}
                onChange={(e) => setAttachmentForm({ ...attachmentForm, name: e.target.value })}
                placeholder="e.g., 600mm GP Bucket"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Type *</Label>
              <Select
                value={attachmentForm.attachment_type_id}
                onValueChange={(v) => setAttachmentForm({ ...attachmentForm, attachment_type_id: v })}
                required
              >
                <SelectTrigger data-testid="attachment-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {attachmentTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {attachmentTypes.length === 0 && (
                <p className="text-xs text-amber-500">
                  No attachment types found. Add types in Data Management first.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Size</Label>
              <Input
                data-testid="attachment-size-input"
                value={attachmentForm.size}
                onChange={(e) => setAttachmentForm({ ...attachmentForm, size: e.target.value })}
                placeholder="e.g., 600mm, 1200mm, 2T"
              />
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Status</Label>
              <Select
                value={attachmentForm.status}
                onValueChange={(v) => setAttachmentForm({ ...attachmentForm, status: v })}
              >
                <SelectTrigger data-testid="attachment-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="in_use">In Use</SelectItem>
                  <SelectItem value="under_repair">Under Repair</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Notes</Label>
              <Textarea
                value={attachmentForm.notes}
                onChange={(e) => setAttachmentForm({ ...attachmentForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAttachmentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" disabled={!attachmentForm.name || !attachmentForm.attachment_type_id}>
                {editingAttachment ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deleteType === 'equipment' ? 'Delete Asset' : 'Delete Attachment'}
        description={
          deleteType === 'equipment' 
            ? `Are you sure you want to delete "${itemToDelete?.asset_id || itemToDelete?.name}"? This action cannot be undone.`
            : `Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`
        }
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        testId="delete-confirmation-dialog"
      />
    </div>
  );
};

export default Equipment;
